"use client"
import IconArrowDown from "@/app/(site)/_components/Atoms/Icons/IconArrowDown"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import { useKeyPress } from "@/app/(site)/_utils/_hooks/useKeyPres"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { t } from "@/translations"

import placeholder from "@assets/images/placeholder.png"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React from "react"

type LensGuideModalProps = {
    product: ProductData
}

export default function LensGuideModal({ product }: LensGuideModalProps) {
    const [isAnimating, setIsAnimating] = React.useState(false)
    const toggleLensGuideModal = useEcommerceStore(state => state.toggleLensGuideModal)
    const closeLenGuideModal = useEcommerceStore(state => state.closeLensGuideModal)
    const isLensGuideModalOpen = useEcommerceStore(state => state.isLensGuideModalOpen)
    const modalRef = React.useRef<HTMLElement>(null)
    const pathname = usePathname()
    const langCode = getLangCode(pathname.split("/")[1]) || ""

    // Function to toggle body scroll
    const toggleBodyScroll = (isModalOpen: boolean) => {}
    useClickOutside({ refs: [modalRef], onOutsideClick: toggleLensGuideModal })
    useKeyPress({ targetKeys: ["Escape", "Backspace"], action: closeLenGuideModal })

    React.useEffect(() => {
        // Disable scroll when component mounts
        toggleBodyScroll(isLensGuideModalOpen)

        // Enable scroll when component unmounts
        return () => toggleBodyScroll(false)
    }, [isLensGuideModalOpen])

    if (!isLensGuideModalOpen) return null

    function downloadPDF(url: string): void {
        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = blobUrl
                link.download = url.split("/").pop() || "download.pdf"
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
            })
            .catch(error => console.error("Error downloading the PDF:", error))
    }

    const blurClasses = isAnimating ? "opacity-100" : "opacity-100"

    const lensGuideImageUrl = product?.lens_guide_type?.image?.url || placeholder
    const lensGuidePDFUrl = product?.lens_guide_type?.document?.url ?? ""
    const productImage = product?.media?.standard?.[0]?.url || placeholder
    const productName = product?.name || ""
    const productLensName = product?.lens?.lens_name || ""

    return (
        <div>
            <div
                className={`fixed top-0 z-30 h-full w-[100%] bg-black/20 backdrop-blur-md transition-all duration-300 ease-in-out ${blurClasses}`}
            />

            <div
                ref={modalRef as React.RefObject<HTMLDivElement>}
                className="fixed bottom-0 left-0 right-0 top-0 z-40 flex h-full overflow-y-auto bg-main-white px-16 py-16 sm:left-auto sm:w-552 md:bottom-auto md:px-24 md:py-32"
            >
                <div className="flex w-full flex-col justify-between">
                    <div>
                        <div className="mb-24 flex items-center justify-between">
                            <Text
                                size="extrasmall"
                                weight="regular"
                                className="col-span-5 md:!text-16"
                            >
                                {t(langCode, "product_page.lens_guide")}
                            </Text>
                            <div
                                className="col-span-1 flex cursor-pointer justify-end"
                                onClick={() => toggleLensGuideModal()}
                            >
                                <IconClose />
                            </div>
                        </div>
                        <div className="mb-24 flex w-full gap-16 border-b border-b-black">
                            <div className="mb-17 w-104 bg-gray-100">
                                <Image
                                    src={productImage}
                                    alt={productName}
                                    width={500}
                                    height={500}
                                />
                            </div>
                            <div className="flex flex-col justify-between">
                                <div>
                                    <Text
                                        size="extrasmall"
                                        weight="regular"
                                        className="text-main-dark-grey md:!text-16"
                                    >
                                        {t(langCode, "product_page.selected_lens")}
                                    </Text>
                                    <Text
                                        size="extrasmall"
                                        weight="regular"
                                        className="md:!text-16"
                                    >
                                        {productLensName}
                                    </Text>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Image
                                src={lensGuideImageUrl}
                                alt={product.name}
                                width={4000}
                                height={4000}
                                quality={100}
                            />
                        </div>
                    </div>
                    <div className="col-span-6"></div>
                    <div className="w-full">
                        <button
                            onClick={() => downloadPDF(lensGuidePDFUrl)}
                            className="my-32 flex items-center gap-8"
                            aria-label="Download PDF"
                        >
                            <Text
                                size="extrasmall"
                                weight="regular"
                                className="text-main-dark-grey md:!text-16"
                                role="text"
                                aria-hidden="true"
                            >
                                {t(langCode, "product_page.download_pdf")}
                            </Text>
                            <IconArrowDown aria-hidden="true" className="h-16 w-16" />
                        </button>
                        <ButtonPill
                            color="black"
                            variant="primary"
                            size="large"
                            aria-label="Close"
                            className="mb-16 w-full md:mb-0"
                            onClick={() => toggleLensGuideModal()}
                        >
                            {t(langCode, "common.close")}
                        </ButtonPill>
                    </div>
                </div>
            </div>
        </div>
    )
}
